import moment from 'moment';
import * as _ from 'lodash';
import DDLogs from '@/DDLogs';
import { isIosNativeApp } from '@/utils/deviceUtils';
import TrialSettings from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/TrialSettings.vue';
import DormantAfterTrialSettings
  from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/DormantAfterTrialSettings.vue';
import SubscribedSettings
  from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/SubscribedSettings.vue';
import CanceledSettings
  from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/CanceledSettings.vue';
import FreeSubscriptionSettings
  from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/FreeSubscriptionSettings.vue';
import OnboardingSettings
  from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/OnboardingSettings.vue';
import { TRIAL_PERIODS } from '@/constants/subscription-state';
import subscriptionStateApi from '../../api/SubscriptionStateApi';

const LOCAL_STORAGE_KEY = 'riseup.subscriptionCtaDismissedData';

const SUBSCRIPTION_STATES = {
  TRIAL: 'trial',
  FREE: 'free',
  SUBSCRIBED: 'subscribed',
  DORMANT_AFTER_CANCELED: 'dormant-after-canceled',
  DORMANT_AFTER_TRIAL: 'dormant-after-trial',
  CANCELED: 'canceled',
  PRE_TRIAL: 'pre-trial',
};

const SUBSCRIPTION_SETTINGS_COMPONENT_MAP = {
  [SUBSCRIPTION_STATES.FREE]: FreeSubscriptionSettings,
  [SUBSCRIPTION_STATES.PRE_TRIAL]: OnboardingSettings,
  [SUBSCRIPTION_STATES.TRIAL]: TrialSettings,
  [SUBSCRIPTION_STATES.SUBSCRIBED]: SubscribedSettings,
  [SUBSCRIPTION_STATES.CANCELED]: CanceledSettings,
  [SUBSCRIPTION_STATES.DORMANT_AFTER_TRIAL]: DormantAfterTrialSettings,
  [SUBSCRIPTION_STATES.DORMANT_AFTER_CANCELED]: CanceledSettings,
};

const state = {
  since: null,
  until: null,
  isDormant: false,
  inTrial: false,
  isFree: false,
  isAutoRenewalOn: false,
  hasAccessToCashflow: false,
  subscriptionSettingsComponent: null,
};

function _shouldShowCta({ inTrial, trialPeriod }) {
  if (isIosNativeApp()) {
    return false;
  }
  if (inTrial) {
    const subscriptionDismissedDataString = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (subscriptionDismissedDataString) {
      const { periodDismissed } = JSON.parse(subscriptionDismissedDataString);
      if (!periodDismissed) {
        return true;
      }
      return trialPeriod !== periodDismissed;
    }
    return true;
  }
  return false;
}

const getters = {
  daysLeft: state => Math.max(moment(state.until).diff(moment(), 'days') + 1, 0),
  trialLength: state => moment(state.until).diff(moment(state.since), 'days'),
  trialPeriod: (state, getters) => {
    if (getters.inTrial) {
      const diffWithUntil = moment(state.until).diff(moment(), 'days');
      if (diffWithUntil > 20) {
        return TRIAL_PERIODS.BEGINNING;
      } if (diffWithUntil <= 20 && diffWithUntil > 7) {
        return TRIAL_PERIODS.MIDDLE;
      }
      // 7 days left for the trial
      return TRIAL_PERIODS.FINAL;
    }
    return undefined;
  },
  hasAccessToCashflow: state => state.hasAccessToCashflow,
  isAutoRenewalOn: state => state.isAutoRenewalOn,
  inTrial: state => state.inTrial,
  isFree: state => state.isFree,
  isDormant: state => state.isDormant,
  shouldShowCta: (state, getters) => _shouldShowCta({
    inTrial: getters.inTrial,
    trialPeriod: getters.trialPeriod,
  }),
  subscriptionSettingsComponent: state => state.subscriptionSettingsComponent,
};

const actions = {
  async fetchSubscriptionState({ commit }) {
    const simplifiedSubscriptionState = await subscriptionStateApi.simplifiedSubscriptionState();
    commit('setSimplifiedSubscriptionState', simplifiedSubscriptionState);
  },
  async fetchSubscriptionSettingsComponent({ commit }) {
    const subscriptionState = await subscriptionStateApi.fetchSubscriptionState();
    // Notice how we very intentionally don't save the subscriptionState.state in the store.
    // This is done because the subscriptionState is very low level and it might be changed in the backend, we want
    // to make sure we make most of the decisions based on the aggregated simplified state, and the only place (for now)
    // that the subscription state actually matters one-to-one is the settings component, which maybe in the future
    // will rely on a different data than the subscription state to decide what to show to the user
    commit('setSubscriptionSettingsComponent', SUBSCRIPTION_SETTINGS_COMPONENT_MAP[subscriptionState.state]);
  },
  setSimplifiedSubscriptionState({ commit }, simplifiedSubscriptionState) {
    commit('setSimplifiedSubscriptionState', simplifiedSubscriptionState);
  },
  ctaDismissed({ getters }) {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ dismissDate: new Date(), periodDismissed: getters.trialPeriod }));
    } catch (error) {
      DDLogs.error(error);
    }
  },
};

// mutations
const mutations = {
  setSubscriptionSettingsComponent(state, component) {
    state.subscriptionSettingsComponent = component;
  },
  setHasAccessToCashflow(state, hasAccessToCashflow) {
    state.hasAccessToCashflow = hasAccessToCashflow;
  },
  setAutoRenewalOn(state, isAutoRenewalOn) {
    state.isAutoRenewalOn = isAutoRenewalOn;
  },
  setSimplifiedSubscriptionState(state, { since, until, isDormant, inTrial, isFree, isAutoRenewalOn, hasAccessToCashflow }) {
    state.since = since;
    state.until = until;
    state.isDormant = isDormant;
    state.inTrial = inTrial;
    state.isFree = isFree ?? false;
    state.isAutoRenewalOn = isAutoRenewalOn;
    state.hasAccessToCashflow = hasAccessToCashflow;
  },
};

export default {
  namespaced: true,
  SETTINGS_COMPONENTS: _.uniq(_.values(SUBSCRIPTION_SETTINGS_COMPONENT_MAP)),
  state,
  actions,
  mutations,
  getters,
};
